// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Region } from '../types';
import { num, rate, delta, esc, tip, STATE_ORDER } from '../format';
import { gloss } from '../glossary';
import { binValues } from '../utils/histogram';
import { openRegion } from '../main';

type Metric = 'access' | 'now' | 'lost' | 'pct';
const METRICS: { id: Metric; label: string; asc: boolean }[] = [
  { id: 'lost', label: 'Branches lost', asc: false },
  { id: 'access', label: 'Branches / 10k', asc: false },
  { id: 'now', label: 'Branches now', asc: false },
  { id: 'pct', label: '% closed', asc: true },
];

export function renderRankings(root: HTMLElement, data: Dataset): void {
  let metric: Metric = 'lost';
  let stateF = 'all';
  let binFilter: [number, number] | null = null;

  const valueOf = (r: Region): number | null => {
    switch (metric) {
      case 'access': return r.perCapita;
      case 'now': return r.branchNow;
      case 'lost': return -r.branchChangeAbs; // positive = branches lost
      case 'pct': return r.branchChange; // negative = closed
    }
  };

  root.innerHTML = `
    <div class="view-head">
      <h2>Regional rankings</h2>
      <p>Every ${gloss('sa3', 'SA3 region')} ranked. Access is ${gloss('perCapita', 'branches per 10,000 residents')}; "branches lost" and "% closed" measure the retreat since ${data.national.years[0]}. Click a region for its full record; click a histogram bar to filter the list to that band.</p>
    </div>
    <div class="controls">
      <span class="control-label">Rank by</span>
      <div class="seg" aria-label="Metric">${METRICS.map((m) => `<button data-metric="${m.id}" aria-pressed="${m.id === metric}">${m.label}</button>`).join('')}</div>
      <select id="state"><option value="all">All states</option>${STATE_ORDER.map((s) => `<option value="${s}">${s}</option>`).join('')}</select>
    </div>
    <div class="two-col">
      <div class="panel">
        <div class="panel-head"><h3 id="lb-title"></h3><p id="lb-sub"></p></div>
        <div class="barlist" id="bars"></div>
      </div>
      <div class="panel">
        <div class="panel-head"><h3>Distribution</h3><p>How all regions spread across this measure. Click a bar to filter the leaderboard; click again to clear.</p></div>
        <div class="chart-wrap" id="hist"></div>
        <p class="mini-note" id="hist-note"></p>
      </div>
    </div>
  `;

  const barsEl = root.querySelector('#bars') as HTMLElement;
  const histEl = root.querySelector('#hist') as HTMLElement;

  function pool(): Region[] {
    return data.regions.filter((r) =>
      (stateF === 'all' || r.state === stateF) &&
      valueOf(r) != null &&
      (metric === 'access' ? (r.pop ?? 0) > 3000 : true) &&
      (metric === 'lost' ? r.branchChangeAbs < 0 : true) &&
      (metric === 'now' ? r.branchNow > 0 : true),
    );
  }

  function fmt(r: Region): string {
    switch (metric) {
      case 'access': return rate(r.perCapita, 2);
      case 'now': return num(r.branchNow);
      case 'lost': return num(-r.branchChangeAbs);
      case 'pct': return delta(r.branchChange);
    }
  }

  function draw(): void {
    const p = pool();
    const m = METRICS.find((x) => x.id === metric)!;
    const values = p.map((r) => valueOf(r)!) as number[];
    const median = values.length ? [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)] : 0;

    // histogram over the metric's natural value (access/now/lost/pct with real signs)
    const histValues = p.map((r) => metric === 'lost' ? -r.branchChangeAbs : metric === 'pct' ? r.branchChange! * 100 : valueOf(r)!);
    const bins = binValues(histValues, 12);
    const maxCount = Math.max(...bins.map((b) => b.count), 1);
    const hw = 460, hh = 190, hm = { t: 8, r: 10, b: 30, l: 30 };
    const iw = hw - hm.l - hm.r, ih = hh - hm.t - hm.b;
    const bw = iw / bins.length;
    let hbars = '';
    bins.forEach((b, i) => {
      const bh = (b.count / maxCount) * ih;
      const x = hm.l + i * bw;
      const active = binFilter && binFilter[0] === b.x0;
      hbars += `<rect class="bar click" x="${(x + 1).toFixed(1)}" y="${(hm.t + ih - bh).toFixed(1)}" width="${(bw - 2).toFixed(1)}" height="${Math.max(0, bh).toFixed(1)}" rx="1.5" fill="${active ? 'var(--accent-warm)' : 'var(--sev-4)'}" data-lo="${b.x0}" data-hi="${b.x1}" data-tip="${tip(`${fmtBin(b.x0)}–${fmtBin(b.x1)}: ${b.count} region${b.count === 1 ? '' : 's'}`)}" />`;
    });
    const axisY = hm.t + ih;
    let hlabels = '';
    for (let i = 0; i <= bins.length; i += 3) {
      const x = hm.l + i * bw;
      const v = bins[0] ? bins[0].x0 + (bins[0].x1 - bins[0].x0) * i : 0;
      hlabels += `<text class="axis-text" x="${x.toFixed(1)}" y="${axisY + 14}" text-anchor="middle">${fmtBin(v)}</text>`;
    }
    histEl.innerHTML = `<svg class="chart" viewBox="0 0 ${hw} ${hh}" role="img" aria-label="Distribution histogram"><line class="axis-line" x1="${hm.l}" y1="${axisY}" x2="${hm.l + iw}" y2="${axisY}" />${hbars}${hlabels}</svg>`;
    histEl.querySelectorAll<SVGRectElement>('[data-lo]').forEach((rect) =>
      rect.addEventListener('click', () => {
        const lo = +rect.dataset.lo!, hi = +rect.dataset.hi!;
        binFilter = binFilter && binFilter[0] === lo ? null : [lo, hi];
        draw();
      }),
    );

    // leaderboard (respect bin filter)
    let ranked = [...p].sort((a, b) => (m.asc ? (valueOf(a)! - valueOf(b)!) : (valueOf(b)! - valueOf(a)!)));
    if (binFilter) {
      ranked = ranked.filter((r) => {
        const v = metric === 'lost' ? -r.branchChangeAbs : metric === 'pct' ? r.branchChange! * 100 : valueOf(r)!;
        return v >= binFilter![0] && v <= binFilter![1];
      });
    }
    const top = ranked.slice(0, 30);
    const scaleMax = Math.max(...top.map((r) => Math.abs(metric === 'pct' ? r.branchChange! : valueOf(r)!)), 0.0001);
    barsEl.innerHTML = top.map((r, i) => {
      const raw = metric === 'pct' ? Math.abs(r.branchChange!) : Math.abs(valueOf(r)!);
      const colour = metric === 'now' || metric === 'access' ? 'var(--branch)' : 'var(--accent-warm)';
      return `<div class="barrow" data-code="${esc(r.code)}" data-tip="${tip(`${r.name} (${r.state})\n${num(r.branch2017)} → ${num(r.branchNow)} branches`)}">
        <span class="lbl"><span class="rank">${i + 1}</span> ${esc(r.name)}</span>
        <span class="track"><span style="width:${((raw / scaleMax) * 100).toFixed(1)}%;background:${colour}"></span></span>
        <span class="val">${fmt(r)}</span>
      </div>`;
    }).join('');
    barsEl.querySelectorAll<HTMLElement>('[data-code]').forEach((el) => el.addEventListener('click', () => openRegion(el.dataset.code!)));

    (root.querySelector('#lb-title') as HTMLElement).textContent =
      metric === 'access' ? 'Best branch access per resident'
      : metric === 'now' ? 'Most branches'
      : metric === 'lost' ? 'Biggest branch losses'
      : 'Steepest closures';
    (root.querySelector('#lb-sub') as HTMLElement).innerHTML =
      `${p.length} regions · national median ${metric === 'pct' ? delta(median) : metric === 'access' ? rate(median, 2) + ' / 10k' : num(median)}` + (binFilter ? ` · filtered to ${fmtBin(binFilter[0])}–${fmtBin(binFilter[1])}` : '');
    (root.querySelector('#hist-note') as HTMLElement).textContent = `${p.length} regions across ${bins.length} bands.`;
  }

  function fmtBin(v: number): string {
    if (metric === 'pct') return `${v.toFixed(0)}%`;
    if (metric === 'access') return v.toFixed(1);
    return num(Math.round(v));
  }

  root.querySelectorAll<HTMLButtonElement>('[data-metric]').forEach((b) =>
    b.addEventListener('click', () => { metric = b.dataset.metric as Metric; binFilter = null; root.querySelectorAll<HTMLButtonElement>('[data-metric]').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); draw(); }),
  );
  (root.querySelector('#state') as HTMLSelectElement).addEventListener('change', (e) => { stateF = (e.target as HTMLSelectElement).value; binFilter = null; draw(); });

  draw();
}
